import React, { useEffect, useState } from "react";
import {
  Container, Card, CardBody, Badge, Spinner, Button,
  Form, Input, Label, FormGroup, Alert,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useRoleContext } from "../../context/RoleContext";
import { useAuthUser } from "../../hooks/AuthProvider";
import {
  getPropertyById,
  createPropertyTicket,
  getMyPropertyTickets,
  notifyPropertyTicketParticipants,
} from "../../helpers/firestoreService";

const SEVERITY_COLORS = { Low: "info", Medium: "warning", High: "orange", Urgent: "danger" };
const STATUS_COLORS = { Open: "primary", "In Progress": "warning", Resolved: "success", Closed: "secondary" };

const TenantTickets = () => {
  document.title = "My Tickets | Porcher";

  const { activeTenant } = useRoleContext();
  const user = useAuthUser();
  const navigate = useNavigate();

  const [property, setProperty] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("Low");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState(false);

  const load = async () => {
    if (!user?.uid || !activeTenant) return;
    setLoading(true);
    try {
      const [prop, myTickets] = await Promise.all([
        activeTenant.propertyId ? getPropertyById(activeTenant.propertyId) : null,
        getMyPropertyTickets(user.uid),
      ]);
      setProperty(prop);
      setTickets(myTickets);
    } catch (e) {
      console.error("TenantTickets load error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [user, activeTenant]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      setFormError("Subject and description are required.");
      return;
    }
    setFormError("");
    setSubmitting(true);
    try {
      const { id: ticketId } = await createPropertyTicket({
        propertyId: activeTenant?.propertyId || "",
        propertyAddress: property?.address || "",
        tenantId: activeTenant?.id || "",
        tenantName: activeTenant?.name || "",
        createdByUid: user.uid,
        createdByEmail: user.email,
        title: title.trim(),
        description: description.trim(),
        severity,
      });
      notifyPropertyTicketParticipants(activeTenant?.propertyId, {
        type: "ticket_created",
        title: "New ticket from a tenant",
        message: `New ticket: ${title.trim()}`,
        ticketId,
        excludeUid: user.uid,
      }).catch((err) => console.error("Notify owners error:", err));

      setTitle("");
      setDescription("");
      setSeverity("Low");
      setShowForm(false);
      setFormSuccess(true);
      setTimeout(() => setFormSuccess(false), 4000);
      await load();
    } catch (err) {
      console.error("Create ticket error:", err);
      setFormError(err.message || "Failed to submit ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">My Tickets</h4>
          {activeTenant?.propertyId && (
            <Button color="primary" size="sm" onClick={() => setShowForm((v) => !v)}>
              <i className="bx bx-plus me-1" />
              New Ticket
            </Button>
          )}
        </div>

        {formSuccess && (
          <Alert color="success" className="mb-3">
            <i className="bx bx-check-circle me-2" />Ticket submitted successfully.
          </Alert>
        )}

        {!activeTenant?.propertyId ? (
          <div className="text-center py-5 text-muted">
            <i className="bx bx-error-circle font-size-40 d-block mb-3" />
            <h5>Not linked to a property yet</h5>
            <p className="small">Ask your property owner to assign you to a property before submitting a ticket.</p>
          </div>
        ) : (
        <>
        {showForm && (
          <Card className="border-0 shadow-sm mb-4">
            <CardBody>
              <h6 className="mb-3">Submit a New Ticket</h6>
              {formError && <Alert color="danger" className="small py-2">{formError}</Alert>}
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label className="small fw-semibold">Subject</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Brief description of the issue"
                  />
                </FormGroup>
                <FormGroup>
                  <Label className="small fw-semibold">Description</Label>
                  <Input
                    type="textarea"
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Provide details about your request…"
                  />
                </FormGroup>
                <FormGroup>
                  <Label className="small fw-semibold">Urgency</Label>
                  <Input type="select" value={severity} onChange={(e) => setSeverity(e.target.value)}>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </Input>
                </FormGroup>
                <div className="d-flex gap-2">
                  <Button color="primary" type="submit" size="sm" disabled={submitting}>
                    {submitting ? <Spinner size="sm" /> : "Submit"}
                  </Button>
                  <Button color="secondary" outline size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        )}

        {tickets.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bx bx-task font-size-40 d-block mb-3" />
            <h5>No tickets yet</h5>
            <p className="small">Submit a ticket to report an issue or request assistance.</p>
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardBody className="p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="small">Subject</th>
                      <th className="small">Urgency</th>
                      <th className="small">Status</th>
                      <th className="small">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((t) => (
                      <tr
                        key={t.id}
                        role="button"
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/tenant/tickets/${t.id}`)}
                      >
                        <td className="small">{t.title}</td>
                        <td>
                          <Badge color={SEVERITY_COLORS[t.severity] || "secondary"} pill className="small">
                            {t.severity || "Low"}
                          </Badge>
                        </td>
                        <td>
                          <Badge color={STATUS_COLORS[t.status] || "secondary"} pill className="small">
                            {t.status}
                          </Badge>
                        </td>
                        <td className="small text-muted">
                          {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        )}
        </>
        )}
      </Container>
    </div>
  );
};

export default TenantTickets;

